import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';

import { CHART_END_DATE_ROLES, ADMIN_ROLE, todayIso } from '../utils/chartEndDates';

/**
 * THE CHART END DATE, one per role. Admin only.
 *
 * <h2>What a date here does</h2>
 *
 * The server cuts every bar read for a user in that role at it. A date in the PAST truncates
 * history and leaves no future bars; a date in the FUTURE leaves the series whole and extends
 * the forward axis so studies have room to project into. The panel says both, because a reader
 * setting a date a year out and seeing no new bars would reasonably conclude it had not worked.
 *
 * <h2>⚠ THERE IS NO FIELD FOR Admin, and that is the design rather than an omission</h2>
 *
 * An administrator configures these. A date they were themselves subject to would mean setting
 * the restriction through the restriction — and more practically, an admin checking whether a
 * customer's date works needs to be able to see the bars it hides. The panel states it, so the
 * absent field reads as a decision rather than as a form somebody forgot to finish.
 *
 * <h2>Blank means NO RESTRICTION, and it is the only way to clear one</h2>
 *
 * So the save REPLACES all four rather than merging: a merge makes "remove this date" an
 * operation the form cannot express, and that is the one field a reader most needs to undo.
 *
 * <h2>The admin gate is the SERVER'S, not this component's</h2>
 *
 * This returns null for a non-admin, and so does the panel that opens it — but both are
 * conveniences. `PUT /api/chart-window/settings` answers 403 with a reason, because anyone can
 * put to an endpoint and a hidden control is not an inaccessible one.
 */
const ChartEndDateDialog = ({ open, onClose, isAdmin, api }) => {
  const [dates, setDates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/chart-window');
      // `settings` is present only for an admin. Falling back to {} rather than failing: the
      // gate is the server's, and an admin whose own request came back without it has a real
      // problem the empty form will make obvious.
      setDates({ ...((res.data && res.data.settings) || {}) });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not read the current settings.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Re-read on every OPEN rather than once on mount. Another admin may have changed a date, and
  // a form showing a stale value would overwrite their change with it on the next save.
  useEffect(() => { if (open && isAdmin) load(); }, [open, isAdmin, load]);

  if (!isAdmin) return null;

  const set = (role, value) => {
    setSaved(null);
    setDates((prev) => ({ ...prev, [role]: value }));
  };

  /*
   * ⚠ THERE IS NO CLIENT-SIDE DATE VALIDATION HERE, and that is a decision rather than a gap.
   *
   * The first version had one -- an alert naming any field whose value was not an ISO date --
   * and it could never fire. An `<input type="date">` holds a valid `yyyy-MM-dd` or the empty
   * string and nothing else; the browser will not accept `2026-02-30` into one. The other way a
   * value reaches this form is the GET, and `ChartEndDateService` drops a date it cannot parse
   * before the response is built. Both sources are closed.
   *
   * So the check was an alert nobody could ever see, which is the unreachable-control defect
   * this codebase keeps recording -- and it could not be tested, which is how it was noticed.
   * `isIsoDate` stays in `chartEndDates`, where `weekdaysBetween` genuinely does guard against a
   * value from the API and the chart's own axis.
   *
   * The SERVER still validates. `parseDate` answers 400 with the offending value named, because
   * a request does not have to come from this form.
   */

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const body = {};
      CHART_END_DATE_ROLES.forEach((r) => {
        const v = (dates[r.role] || '').trim();
        // NULL rather than omitted for a blank. The server replaces the whole document, so an
        // omitted key and a null one mean the same thing here -- but sending it explicitly is
        // what makes a captured request say "this role has no restriction" rather than leaving
        // a reader to infer it from an absence.
        body[r.role] = v === '' ? null : v;
      });
      const res = await api.put('/api/chart-window/settings', body);
      setDates({ ...((res.data && res.data.settings) || {}) });
      setSaved('Saved. New chart loads are cut at these dates; a chart already on screen keeps '
        + 'what it has until it is reloaded.');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const today = todayIso();

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth
      data-testid="chart-end-date-dialog">
      <DialogTitle sx={{ pb: 0.5 }}>
        Chart end date
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Where each audience&apos;s charts stop. Blank means no restriction.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }} data-testid="chart-end-date-explainer">
          A date in the <b>past</b> truncates history there and leaves no future bars. A date in
          the <b>future</b> leaves the whole series and extends the forward axis to it, so studies
          can project into the empty space — no prices are invented. Today is {today}.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {CHART_END_DATE_ROLES.map((r) => (
            <Box key={r.role}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label={r.label}
                value={dates[r.role] || ''}
                disabled={loading || saving}
                onChange={(e) => set(r.role, e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': `chart-end-date-${r.role}` }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', pl: 0.5 }}>
                {r.blurb}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 2 }}
          data-testid="chart-end-date-admin-note">
          {ADMIN_ROLE} has no field: an administrator&apos;s charts are never cut off, so that
          the bars a customer&apos;s date hides stay visible to whoever set it.
        </Typography>

        {error && <Alert severity="error" sx={{ mt: 2, py: 0.5 }} data-testid="chart-end-date-error">{error}</Alert>}
        {saved && <Alert severity="success" sx={{ mt: 2, py: 0.5 }} data-testid="chart-end-date-saved">{saved}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small" disabled={saving}>Close</Button>
        <Button onClick={save} size="small" variant="contained"
          disabled={loading || saving}
          data-testid="chart-end-date-save">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChartEndDateDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Renders nothing when false. The SERVER is the gate — see the component note. */
  isAdmin: PropTypes.bool,
  /** The authenticated axios instance. */
  api: PropTypes.shape({ get: PropTypes.func, put: PropTypes.func }).isRequired,
};

ChartEndDateDialog.defaultProps = {
  open: false,
  isAdmin: false,
};

export default ChartEndDateDialog;
